import { unsetRating } from './access';
import { isKinkRated } from './stats';
import type { Category, Id, Kink, KinklistDoc } from './types';

export type QuickFilter = 'all' | 'unrated' | 'commented';

/** `[start, end)` ranges in the *original* string. */
export type Range = readonly [start: number, end: number];

interface Folded {
  text: string;
  /** For each code unit of `text`: `[start, end)` of the original character it came from. */
  origin: Range[];
}

/**
 * Case- and diacritic-insensitive folding that remembers where each folded
 * character came from, so matches can be highlighted in the original text.
 */
export function fold(value: string): Folded {
  let text = '';
  const origin: Range[] = [];
  let index = 0;
  for (const char of value) {
    const folded = char.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase().replace('ß', 'ss');
    const range: Range = [index, index + char.length];
    origin.push(...Array.from({ length: folded.length }, () => range));
    text += folded;
    index += char.length;
  }
  return { text, origin };
}

export function normalizeQuery(query: string): string {
  return fold(query.trim().replace(/\s+/g, ' ')).text;
}

const MARK = /^\p{M}/u;

/** Every non-overlapping occurrence of `query` (already normalized) in `value`. */
export function findMatches(value: string, query: string): Range[] {
  if (query === '') return [];
  const { text, origin } = fold(value);
  const ranges: Range[] = [];
  for (let at = text.indexOf(query); at !== -1; at = text.indexOf(query, at + query.length)) {
    const start = origin[at]?.[0] ?? 0;
    let end = origin[at + query.length - 1]?.[1] ?? start;
    // Keep trailing combining marks with their base character.
    while (MARK.test(value.slice(end))) end += value.slice(end).search(/[^\p{M}]|$/u);
    ranges.push([start, end]);
  }
  return ranges;
}

export interface VisibleKink {
  kink: Kink;
  matches: Range[];
  /**
   * Shown dimmed instead of hidden: fully rated under "Unrated" (which never
   * hides anything), or kept under "Has comment" after its comment was removed.
   */
  dimmed: boolean;
}

export interface VisibleCategory {
  category: Category;
  kinks: VisibleKink[];
  /** True when the category is shown expanded (collapsed categories open while searching). */
  expanded: boolean;
}

export interface FilterOptions {
  query: string;
  filter: QuickFilter;
  /**
   * Kinks that passed the quick filter when it was applied (`passingKinks`).
   * They stay visible (dimmed) so rows don't vanish while being edited.
   */
  keep?: ReadonlySet<Id>;
}

function passesFilter(kink: Kink, filter: QuickFilter, unsetId: Id): boolean {
  if (filter === 'unrated') return !isKinkRated(kink, unsetId);
  if (filter === 'commented') return kink.comment !== undefined;
  return true;
}

/** Ids of the kinks that pass `filter` right now (snapshot for `FilterOptions.keep`). */
export function passingKinks(doc: KinklistDoc, filter: QuickFilter): Set<Id> {
  const unsetId = unsetRating(doc.ratings).id;
  return new Set(
    doc.categories.flatMap((c) =>
      c.kinks.filter((k) => passesFilter(k, filter, unsetId)).map((k) => k.id),
    ),
  );
}

/**
 * Applies search + quick filter. "Unrated" hides nothing: it dims what's fully
 * rated. "Has comment" hides kinks without comments (except those in `keep`).
 * Categories with no visible kinks are dropped while searching or filtering.
 */
export function filterDoc(doc: KinklistDoc, options: FilterOptions): VisibleCategory[] {
  const query = normalizeQuery(options.query);
  const unsetId: Id = unsetRating(doc.ratings).id;
  const active = query !== '' || options.filter !== 'all';
  return doc.categories.flatMap((category) => {
    const kinks = category.kinks.flatMap((kink): VisibleKink[] => {
      const passes = passesFilter(kink, options.filter, unsetId);
      const kept = options.filter === 'unrated' || (options.keep?.has(kink.id) ?? false);
      if (!passes && !kept) return [];
      const matches = findMatches(kink.name, query);
      return query === '' || matches.length > 0 ? [{ kink, matches, dimmed: !passes }] : [];
    });
    if (active && kinks.length === 0) return [];
    return [{ category, kinks, expanded: query !== '' || !(category.collapsed ?? false) }];
  });
}
